import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useWebSocket } from "../../contexts/WebSocketProvider";
import { transformC2ProfileToCDDRecord } from "../../services/cddRecordsApi";
import { fetchC2Profile } from "../../services/configService";
import useCddRecordsStore from "../../stores/cddRecordsStore";
import {
  AssociatedPerson,
  CDDRecord,
  getSectionsForEntityType,
  isAssociationCDDRecord,
  isCompanyCDDRecord,
  isIndividualCDDRecord,
  isRegisteredBodyCDDRecord,
  isTrustCDDRecord,
  Section
} from "../../types/cdd-records.types";
import { normalizeCDDStatus } from "../../utils/cddStatus";
import { formatDateShort } from "../../utils/dateFormatters";
import PortalHeader from "../portal/PortalHeader";
import { CDDHeader } from "./CDDHeader";
import { CDDNavigation } from "./CDDNavigation";
import {
  exportCDDRecordToCsv,
  exportCDDRecordToJson,
  exportCDDRecordToPdf,
} from "./CDDPdfExport";
import NSWBackgroundWatermark from "./NSWBackgroundWatermark";
import {
  CompanySummary,
  StructureControl,
} from "./sections/company";
import {
  AssociationProfile,
  AssociationStructureMap,
  IndividualProfile,
  RegisteredBodyProfile,
  RegulatorModule,
} from "./sections/entity-specific";
import {
  AssociatedPersons,
  AuditTrail,
  ChecksLog,
  CustomerAssessment,
  Screening,
} from "./sections/shared";
// AL-455: imported directly rather than via the sections barrel, so this
// stays visibly separate from the established sections while the placement is still open.
import CheckAccountsPanel from "./sections/shared/CheckAccountsPanel";
import { TrustProfile, TrustRolesMap } from "./sections/trust";

interface CDDRecordsDetailsProps {
  record?: CDDRecord;
  onBack?: () => void;
}

const CDDRecordsDetails: React.FC<CDDRecordsDetailsProps> = ({
  record: propRecord,
  onBack,
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useWebSocket();
  const prevAuthenticated = useRef(false);
  const [record, setRecord] = useState<CDDRecord | null>(propRecord || null);
  const [activeSection, setActiveSection] = useState<string>("summary");
  const [loading, setLoading] = useState(!propRecord);
  const [error, setError] = useState<string | null>(null);

  const realtimeStatus = useCddRecordsStore((state) => {
    const byRouteId = id ? state.records.get(id) : undefined;
    const matchingRecord = byRouteId ?? state.allRecords.find((candidate) =>
      candidate.id === record?.id ||
      candidate.reference === record?.referenceNumber ||
      candidate.reference === id
    );
    return matchingRecord?.status;
  });

  // Get navigation sections based on entity type
  const getNavigationSections = (): Section[] => {
    if (!record) return [];
    return getSectionsForEntityType(record.entityType).filter(s => s.visible);
  };

  // Initial fetch
  useEffect(() => {
    if (!propRecord && id) {
      fetchRecord(id);
    }
  }, [id, propRecord]);

  // Re-fetch after WebSocket reconnects and re-authenticates
  useEffect(() => {
    if (isAuthenticated && !prevAuthenticated.current && !propRecord && id) {
      fetchRecord(id);
    }
    prevAuthenticated.current = isAuthenticated;
  }, [isAuthenticated]);

  // Apply live status changes immediately so terminal records leave processing UI.
  useEffect(() => {
    if (!realtimeStatus) return;
    setRecord((current) => {
      if (!current) return current;
      const status = normalizeCDDStatus(current.status, realtimeStatus);
      return status === current.status ? current : { ...current, status };
    });
  }, [realtimeStatus]);

  const fetchRecord = async (recordId: string) => {
    setLoading(true);
    setError(null);
    try {
      const wsProfile = await fetchC2Profile(recordId);
      if (wsProfile) {
        const transformed = transformC2ProfileToCDDRecord(wsProfile);
        if (transformed) {
          // AL-352: previously we fetched all risk assessments and matched one
          // to this record by a first-word name / ABN heuristic, then injected
          // its answers into the "Responses" tab. That heuristic mismatched and
          // could show another entity's answers on this compliance record.
          // There is no authoritative link (assessment id) persisted on the CDD
          // record to match on, so the matching + injection has been removed.
          // Risk rating and history come from the C2 profile and are unaffected.
          setRecord(transformed);
          return;
        }
      }
      setError("Record not found");
    } catch (err) {
      setError("Failed to load record");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate("/portal/records");
    }
  };

  const handleSectionChange = (sectionId: string) => {
    setActiveSection(sectionId);
  };

  const handleExport = (format: "pdf" | "csv" | "json") => {
    if (!record) return;
    switch (format) {
      case "pdf":
        exportCDDRecordToPdf(record);
        break;
      case "csv":
        exportCDDRecordToCsv(record);
        break;
      case "json":
        exportCDDRecordToJson(record);
        break;
    }
  };

  const handlePersonClick = (_person: AssociatedPerson) => {
    // Person click handler - no longer navigates to identity verification
  };

  const renderStructure = () => {
    if (!record || !isCompanyCDDRecord(record)) return null;
    return (
      <div className="space-y-6">
        {/* <CompanyStructureMap
          companyName={record.profile.legalName}
          directors={byRole("director")}
          shareholders={byRole("shareholder")}
          beneficialOwners={record.associatedPersons.filter((p) =>
            p.roles?.some(
              (r) =>
                r.toLowerCase().includes("beneficial owner") ||
                r.toLowerCase().includes("beneficial-owner"),
            ),
          )}
          secretaries={byRole("secretary")}
          onPersonClick={handlePersonClick}
        /> */}
        <StructureControl
          structure={record.structure}
          associatedPersons={record.associatedPersons}
          onPersonClick={handlePersonClick}
          /* AL-519 item 7: so the ownership column can name itself correctly. */
          entityType={record.entityType}
        />
      </div>
    );
  };

  const renderRoles = () => {
    if (!record || !isTrustCDDRecord(record)) return null;
    return <TrustRolesMap roles={record.roles} onPersonClick={handlePersonClick} />;
  };

  const renderCommittee = () => {
    if (!record || !isAssociationCDDRecord(record)) return null;
    return (
      <div className="space-y-6">
        {record.committee && (
          <AssociationStructureMap
            associationName={record.profile.name}
            members={record.committee.members}
            positions={record.committee.positions}
            onPersonClick={handlePersonClick}
          />
        )}
        <AssociationProfile profile={record.profile} committee={record.committee} />
      </div>
    );
  };

  const renderLicences = () => {
    if (!record || !isRegisteredBodyCDDRecord(record)) return null;
    return <RegulatorModule licences={record.profile.regulatoryLicences} />;
  };

  const renderAssociatedPersons = () => {
    if (!record) return null;
    return (
      <AssociatedPersons
        persons={record.associatedPersons}
        onPersonClick={handlePersonClick}
      />
    );
  };

  // AL-455: reachable on any case, whatever its status. See the note in
  // getSectionsForEntityType — gating this on the in-progress card alone hid it for most cases.
  const renderCaseProgress = () => {
    if (!id) return null;
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <CheckAccountsPanel caseIdentifier={id} />
      </div>
    );
  };

  const renderScreening = () => {
    if (!record) return null;
    return (
      <Screening
        results={record.screeningResults}
        onResolveMatch={() => {}}
        onInitiateScreening={() => {}}
        recordVerificationStatus={record.verificationStatus}
      />
    );
  };

  const renderAssessment = () => {
    if (!record) return null;
    return (
      <CustomerAssessment
        assessment={record.customerAssessment}
        // AL-438: taken from the record rather than the assessment, so it
        // survives a case that carries no risk assessment block.
        natureOfBusiness={record.natureOfBusiness}
        onUpdateRisk={() => {}}
        onAddAssessment={() => {}}
        recordVerificationStatus={record.verificationStatus}
      />
    );
  };

  const renderChecks = () => {
    if (!record) return null;
    return (
      <ChecksLog
        checksLog={record.checksLog}
        onAddCheck={() => {}}
        onViewCheck={() => {}}
        recordVerificationStatus={record.verificationStatus}
      />
    );
  };

  const renderAudit = () => {
    if (!record) return null;
    return <AuditTrail auditTrail={record.auditTrail} onExport={() => {}} />;
  };

  // Render section content based on active section
  const renderSectionContent = () => {
    if (!record) return null;

    const sectionMap: Record<string, () => React.ReactNode> = {
      summary: renderSummary,
      profile: renderProfile,
      structure: renderStructure,
      roles: renderRoles,
      committee: renderCommittee,
      licences: renderLicences,
      "case-progress": renderCaseProgress,
      "associated-persons": renderAssociatedPersons,
      screening: renderScreening,
      assessment: renderAssessment,
      checks: renderChecks,
      audit: renderAudit,
    };

    return (
      sectionMap[activeSection]?.() ?? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-gray-500">Section content not available</p>
        </div>
      )
    );
  };

  // Render summary section
  const renderSummary = () => {
    if (!record) return null;

    return (
      <div className="space-y-6">
        {/* Record Summary Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 relative overflow-hidden">
          {/* Subtle decorative accent */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"></div>

          <h2 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
            <span className="w-1.5 h-6 bg-gradient-to-b from-blue-500 to-indigo-500 rounded-full"></span>
            Record Summary
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Entity Type
              </p>
              <p className="text-sm font-semibold text-gray-900 capitalize mt-1">
                {record.entityType}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </p>
              <p className="text-sm font-semibold text-emerald-600 capitalize mt-1">
                {record.status}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Risk Level
              </p>
              <p
                className={`text-sm font-semibold capitalize mt-1 ${
                  record.riskLevel === "high"
                    ? "text-rose-600"
                    : record.riskLevel === "medium"
                      ? "text-amber-600"
                      : "text-emerald-600"
                }`}
              >
                {record.riskLevel}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created Date
              </p>
              <p className="text-sm font-semibold text-gray-900 mt-1">
                {formatDateShort(record.createdDate)}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Modified
              </p>
              <p className="text-sm font-semibold text-gray-900 mt-1">
                {record.lastModifiedDate
                  ? formatDateShort(record.lastModifiedDate)
                  : "-"}
              </p>
            </div>
          </div>
        </div>

        {/* Quick Stats - Vibrant Gradient Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Associated Persons - Blue to Indigo */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl blur opacity-75 group-hover:opacity-100 transition-opacity"></div>
            <div className="relative bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 rounded-2xl p-5 border border-blue-400/30 shadow-lg shadow-blue-500/25">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-4xl font-black text-white drop-shadow-lg">
                    {record.associatedPersons.length}
                  </p>
                  <p className="text-sm font-semibold text-blue-100 mt-1">
                    Associated Persons
                  </p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Checks Completed - Emerald to Teal */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl blur opacity-75 group-hover:opacity-100 transition-opacity"></div>
            <div className="relative bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 rounded-2xl p-5 border border-emerald-400/30 shadow-lg shadow-emerald-500/25">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-4xl font-black text-white drop-shadow-lg">
                    {record.checksLog.checks.length}
                  </p>
                  <p className="text-sm font-semibold text-emerald-100 mt-1">
                    Checks Completed
                  </p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Screening Results - Purple to Pink */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl blur opacity-75 group-hover:opacity-100 transition-opacity"></div>
            <div className="relative bg-gradient-to-br from-purple-500 via-purple-600 to-pink-700 rounded-2xl p-5 border border-purple-400/30 shadow-lg shadow-purple-500/25">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-4xl font-black text-white drop-shadow-lg">
                    {record.screeningResults.length}
                  </p>
                  <p className="text-sm font-semibold text-purple-100 mt-1">
                    Screening Results
                  </p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Audit Events - Amber to Orange */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl blur opacity-75 group-hover:opacity-100 transition-opacity"></div>
            <div className="relative bg-gradient-to-br from-amber-500 via-orange-500 to-orange-700 rounded-2xl p-5 border border-amber-400/30 shadow-lg shadow-amber-500/25">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-4xl font-black text-white drop-shadow-lg">
                    {record.auditTrail.entries.length}
                  </p>
                  <p className="text-sm font-semibold text-amber-100 mt-1">
                    Audit Events
                  </p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render profile section based on entity type
  const renderProfile = () => {
    if (!record) return null;

    if (isCompanyCDDRecord(record)) {
      return (
        <CompanySummary
          profile={record.profile}
          riskLevel={record.riskLevel}
          verificationStatus={record.verificationStatus}
        />
      );
    }

    if (isTrustCDDRecord(record)) {
      return <TrustProfile profile={record.profile} />;
    }

    if (isAssociationCDDRecord(record)) {
      return (
        <AssociationProfile
          profile={record.profile}
          committee={record.committee}
        />
      );
    }

    if (isRegisteredBodyCDDRecord(record)) {
      return <RegisteredBodyProfile profile={record.profile} />;
    }

    if (isIndividualCDDRecord(record)) {
      return (
        <IndividualProfile
          profile={record.profile}
          riskLevel={record.riskLevel}
        />
      );
    }

    // Default profile view for other entity types
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Entity Profile
        </h3>
        <p className="text-gray-500">
          Profile information for this entity type
        </p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f6f7]">
        <PortalHeader onLogout={() => navigate("/login")} />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Loading record details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !record) {
    return (
      <div className="min-h-screen bg-[#f5f6f7]">
        <PortalHeader onLogout={() => navigate("/login")} />
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center max-w-md">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Failed to load record
            </h2>
            <p className="text-gray-500 text-sm mb-6">{error}</p>
            <button
              onClick={() => id && fetchRecord(id)}
              className="px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="min-h-screen bg-[#f5f6f7]">
        <PortalHeader onLogout={() => navigate("/login")} />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center max-w-md">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              No record found
            </h2>
            <p className="text-gray-500 text-sm mb-6">
              The requested CDD record could not be found.
            </p>
            <button
              onClick={() => navigate("/portal/records")}
              className="px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Records
            </button>
          </div>
        </div>
      </div>
    );
  }

  const navigationSections = getNavigationSections();
  const isInProgress = record.status === "in-progress";

  return (
    <div className="cdd-details-page min-h-screen bg-transparent relative">
      {/* NSW Style Full-Page Background Watermark */}
      <NSWBackgroundWatermark
        referenceNumber={record.referenceNumber}
        entityType={record.entityType.replace("-", " ").toUpperCase()}
      />

      {/* Portal Header */}
      <PortalHeader onLogout={() => navigate("/login")} />

      {/* CDD Record Header */}
      <CDDHeader record={record} onBack={handleBack} onExport={handleExport} />

      {/* In-progress blocker overlay */}
      {isInProgress && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ top: "160px" }}
        >
          {/* Blurred backdrop */}
          <div className="absolute inset-0 bg-white/60 backdrop-blur-sm" />
          {/* Blocker card */}
          <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 p-10 text-center max-w-lg mx-4">
            <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-5">
              <svg
                className="w-10 h-10 text-blue-500 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Report In Progress
            </h2>
            <p className="text-gray-500 text-sm mb-4">
              This CDD report is still being generated. You will be able to view
              it once processing is complete.
            </p>

            {/*
              AL-455: this card is where a C1 lands when they click an
              in-progress case, and it used to tell them "this usually takes a few minutes ... no
              action is needed from your side". That is wrong for the cases that matter. A case
              waiting on a beneficial owner who never opened their invitation sits here for weeks,
              and the action needed is precisely the C1's — chase their own client. So the copy is
              replaced by the actual answer.

              Placement note for the portal work: the rest of this page is behind a blur-and-block
              overlay while the case is in progress, which means a per-person panel added as a page
              section would be unreachable for exactly the cases it exists to explain.
            */}
            {id && (
              <div className="mb-6 max-h-[45vh] overflow-y-auto">
                <CheckAccountsPanel caseIdentifier={id} compact />
              </div>
            )}

            <p className="text-gray-400 text-xs mb-6">
              You can safely navigate away — this page reflects the current position each time you
              open it.
            </p>
            <button
              onClick={() => navigate("/portal/records")}
              className="px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              ← Back to Records
            </button>
          </div>
        </div>
      )}

      {/* Main content area with sidebar navigation */}
      <div
        className={`max-w-[90rem] mx-auto px-6 py-6 relative z-10 ${isInProgress ? "filter blur-sm pointer-events-none select-none" : ""}`}
      >
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar Navigation */}
          <div className="lg:w-64 flex-shrink-0">
            <CDDNavigation
              sections={navigationSections.map((s) => ({
                id: s.id,
                label: s.label,
                count: s.count,
              }))}
              activeSection={activeSection}
              onSectionChange={handleSectionChange}
            />
          </div>

          {/* Content Area */}
          <div className="flex-1 min-w-0">{renderSectionContent()}</div>
        </div>
      </div>
    </div>
  );
};

export default CDDRecordsDetails;
